import { createContext, useState, useEffect, useContext } from 'react';
import { config } from '../config';
import { createRequestHeaders } from '../utils/domainUtils';

// Early favicon detection and replacement to prevent flashing
const initializeEarlyFavicon = async () => {
  try {
    const hostname = window.location.hostname;

    // Skip API call for localhost/development domains
    if (hostname === 'localhost' || hostname.startsWith('localhost:') ||
        hostname === '127.0.0.1' || hostname.startsWith('127.0.0.1:')) {
      return;
    }

    // Use centralized domain utility for header logic and correct API base
    const headers = createRequestHeaders();
    const response = await fetch(`${config.API_URI}/v1/white-label/branding`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });

    // Handle unauthorized domain access - but not for localhost
    if (response.status === 403) {
      try {
        const errorData = await response.json();
        if (errorData.redirectTo) {
          window.location.href = errorData.redirectTo;
          return;
        }
      } catch (e) {
        console.error('Error parsing 403 response during favicon init:', e);
      }
    }

    if (response.ok) {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        if (data.isWhiteLabel && data.branding && data.branding.favicon) {
          // Immediately replace favicon before React loads
          const faviconLinks = document.querySelectorAll('link[rel*="icon"]');
          faviconLinks.forEach(link => {
            link.href = data.branding.favicon;
          });

          if (faviconLinks.length === 0) {
            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.type = 'image/x-icon';
            newFavicon.href = data.branding.favicon;
            document.head.appendChild(newFavicon);
          }
        }
      }
    }
  } catch (error) {
    console.warn('Early favicon initialization failed:', error);
  }
};

// Run early favicon initialization
if (typeof window !== 'undefined') {
  initializeEarlyFavicon();
}

const WhiteLabelContext = createContext();

// Default branding configuration
const defaultBranding = {
  isWhiteLabel: false,
  appName: 'Zoobbe',
  logo: null,
  logoDark: null,
  logoIcon: null,
  favicon: null,
  primaryColor: '#0966ff',
  secondaryColor: null,
  accentColor: null,
  fontFamily: null,
  hideZoobbeBranding: false,
  customFooter: null,
  supportEmail: null,
  customTermsUrl: null,
  customPrivacyUrl: null,
  features: {
    allowPublicSignup: false
  },
  oauth: {
    enableGoogleAuth: false,
    enableGithubAuth: false,
    googleClientId: null,
    githubClientId: null
  }
};

// Global branding state for cross-component access
let globalBranding = null;

// Apply white label branding to the document with enhanced priority system
const applyBranding = (branding) => {

  // Handle early white label setup cleanup
  if (window.__EARLY_WHITE_LABEL_SETUP__) {

    // Remove temporary favicon
    const tempFavicon = document.getElementById('temp-white-label-favicon');
    if (tempFavicon) {
      tempFavicon.remove();
    }

    // Restore original favicon links
    const hiddenFaviconLinks = document.querySelectorAll('link[rel*="icon"][style*="display: none"]');
    hiddenFaviconLinks.forEach(link => {
      link.style.display = '';
    });
  }

  if (!branding.isWhiteLabel) {

    // If this was a potential white label domain but turned out not to be,
    // restore the default Zoobbe title
    if (window.__POTENTIAL_WHITE_LABEL_DOMAIN__) {
      document.title = 'Zoobbe - Plan. Collaborate. Grow.';
    }
    return;
  }

  globalBranding = branding; // Store globally for access
  const root = document.documentElement;
  

  // Apply custom colors with higher priority to override theme colors
  if (branding.primaryColor) {
    root.style.setProperty('--brand-color', branding.primaryColor, 'important');
    root.style.setProperty('--button-disabled-color', branding.primaryColor + '9c', 'important');
    root.style.setProperty('--hover-brand-color', branding.primaryColor + '9c', 'important');
    root.style.setProperty('--focus-outline-color', branding.primaryColor, 'important');
    root.style.setProperty('--selected-option-text-color', branding.primaryColor, 'important');
    root.style.setProperty('--brand-hover-bg-color', branding.primaryColor + '0d', 'important');
    root.style.setProperty('--select-option-hover-background', branding.primaryColor + '0d', 'important');

    // Apply to all theme color variants to ensure consistency
    root.setAttribute('data-theme-color', 'custom');
  } else {
    // Reset to default if no custom color
    root.style.removeProperty('--brand-color');
    root.style.removeProperty('--button-disabled-color');
    root.style.removeProperty('--hover-brand-color');
    root.style.removeProperty('--focus-outline-color');
    root.style.removeProperty('--selected-option-text-color');
    root.style.removeProperty('--brand-hover-bg-color');
    root.style.removeProperty('--select-option-hover-background');
    root.removeAttribute('data-theme-color');
  }

  if (branding.secondaryColor) {
    root.style.setProperty('--secondary-brand-color', branding.secondaryColor, 'important');
  } else {
    root.style.removeProperty('--secondary-brand-color');
  }

  if (branding.accentColor) {
    root.style.setProperty('--accent-color', branding.accentColor, 'important');
  } else {
    root.style.removeProperty('--accent-color');
  }

  // Apply custom font
  if (branding.fontFamily) {
    root.style.setProperty('--custom-font-family', branding.fontFamily);
    root.style.setProperty('--font-family', branding.fontFamily);
    document.body.style.fontFamily = branding.fontFamily;

    // Apply to all text elements for better coverage
    const style = document.createElement('style');
    style.id = 'white-label-font-override';
    style.textContent = `
      * {
        font-family: ${branding.fontFamily} !important;
      }
    `;

    // Remove existing font override
    const existingFontOverride = document.getElementById('white-label-font-override');
    if (existingFontOverride) {
      existingFontOverride.remove();
    }

    document.head.appendChild(style);
  } else {
    // Reset to default if no custom font
    root.style.removeProperty('--custom-font-family');
    root.style.removeProperty('--font-family');
    document.body.style.fontFamily = '';

    // Remove font override
    const existingFontOverride = document.getElementById('white-label-font-override');
    if (existingFontOverride) {
      existingFontOverride.remove();
    }
  }

  // Apply custom CSS
  if (branding.customCSS) {
    // Remove existing custom CSS
    const existingCustomCSS = document.getElementById('white-label-custom-css');
    if (existingCustomCSS) {
      existingCustomCSS.remove();
    }

    // Add new custom CSS
    const customCSSElement = document.createElement('style');
    customCSSElement.id = 'white-label-custom-css';
    customCSSElement.textContent = branding.customCSS;
    document.head.appendChild(customCSSElement);
  } else {
    // Remove custom CSS if not white label or no custom CSS
    const existingCustomCSS = document.getElementById('white-label-custom-css');
    if (existingCustomCSS) {
      existingCustomCSS.remove();
    }
  }

  // Update document title
  if (branding.appName && branding.appName !== 'Zoobbe') {
    document.title = branding.appName;
  }

  // Update favicon immediately to prevent flashing
  if (branding.favicon) {

    // Immediately update all favicon links
    const faviconLinks = document.querySelectorAll('link[rel*="icon"]');
    faviconLinks.forEach(link => {
      link.href = branding.favicon;
    });

    // If no favicon links exist, create one
    if (faviconLinks.length === 0) {
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.type = 'image/x-icon';
      newFavicon.href = branding.favicon;
      document.head.appendChild(newFavicon);
    }

    // Also update any shortcut icon links
    const shortcutIcons = document.querySelectorAll('link[rel="shortcut icon"]');
    shortcutIcons.forEach(link => {
      link.href = branding.favicon;
    });
  } else if (!branding.isWhiteLabel) {
    // Only reset to default if not white label at all
    const faviconLinks = document.querySelectorAll('link[rel*="icon"]');
    faviconLinks.forEach(link => {
      link.href = '/favicon.ico'; // Default Zoobbe favicon
    });
  }
};

// Fetch white label branding from API
const fetchWhiteLabelBranding = async () => {
  try {
    const hostname = window.location.hostname;

    // Skip API call for localhost/development domains - return default branding
    if (hostname === 'localhost' || hostname.startsWith('localhost:') ||
        hostname === '127.0.0.1' || hostname.startsWith('127.0.0.1:')) {
      return { isWhiteLabel: false, branding: null };
    }

    // Use centralized domain utility for header logic
    const headers = createRequestHeaders();

    const response = await fetch(`${config.API_URI}/v1/white-label/branding`, {
      method: 'GET',
      credentials: 'include',
      headers: headers,
    });


    if (response.ok) {
      const data = await response.json();
      return data;
    } else if (response.status === 403) {
      // Handle unauthorized domain access - but not for localhost
      try {
        const errorData = await response.json();

        // Check if this is an unregistered white label domain
        if (errorData.type === 'white_label_not_found') {
          return {
            isWhiteLabel: false,
            branding: null,
            isUnregisteredDomain: true,
            domain: errorData.domain
          };
        }

        if (errorData.redirectTo) {
          // Perform immediate redirect for unauthorized domains
          window.location.href = errorData.redirectTo;
          return; // This won't execute due to redirect, but for clarity
        }
      } catch (e) {
        console.error('Error parsing 403 response:', e);
      }
      // If no redirect URL, treat as regular error
      return { isWhiteLabel: false, branding: null };
    } else if (response.status === 404) {
      // Check if this is an unregistered white label domain
      try {
        const errorData = await response.json();
        if (errorData.type === 'white_label_not_found') {
          return {
            isWhiteLabel: false,
            branding: null,
            isUnregisteredDomain: true,
            domain: errorData.domain
          };
        }
      } catch (e) {
        // If JSON parsing fails, treat as regular 404
      }
      return { isWhiteLabel: false, branding: null };
    } else {
      // For other error statuses, return default branding
      return { isWhiteLabel: false, branding: null };
    }
  } catch (error) {
    return { isWhiteLabel: false, branding: null };
  }
};

export const WhiteLabelProvider = ({ children }) => {
  const [branding, setBranding] = useState(defaultBranding);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [isUnregisteredDomain, setIsUnregisteredDomain] = useState(false);
  const [domainInfo, setDomainInfo] = useState(null);

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  // Listen for theme changes and reapply branding
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          // Theme changed, reapply white label branding to ensure it takes priority
          if (branding.isWhiteLabel && branding.primaryColor) {
            setTimeout(() => applyBranding(branding), 0);
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, [branding]);

  // Helper function to process branding data
  const processBrandingData = (data) => {
    if (data.isWhiteLabel && data.branding) {
      const processedData = {
        ...defaultBranding,
        ...data.branding,
        isWhiteLabel: true,
        features: {
          ...defaultBranding.features,
          ...data.features
        },
        oauth: {
          ...defaultBranding.oauth,
          ...data.oauth
        }
      };

      return processedData;
    }

    return defaultBranding;
  };

  // Load white label branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        setLoading(true);
        const data = await fetchWhiteLabelBranding();

        // Handle unregistered domain case
        if (data.isUnregisteredDomain) {
          setIsUnregisteredDomain(true);
          setDomainInfo({ domain: data.domain });
          setBranding(defaultBranding);
          applyBranding(defaultBranding);
        } else {
          setIsUnregisteredDomain(false);
          setDomainInfo(null);
          const processedBranding = processBrandingData(data);
          setBranding(processedBranding);
          applyBranding(processedBranding);
        }

        setLastFetchTime(Date.now());
      } catch (err) {
        setError(err.message);
        setBranding(defaultBranding);
        applyBranding(defaultBranding);
      } finally {
        setLoading(false);
      }
    };

    loadBranding();
  }, []);

  // Function to update branding (for settings page)
  const updateBranding = (newBranding) => {
    const updatedBranding = {
      ...branding,
      ...newBranding
    };
    setBranding(updatedBranding);
    applyBranding(updatedBranding);
  };

  // Function to refresh branding from server (with caching)
  const refreshBranding = async (force = false) => {
    // Check if we need to refresh based on cache
    if (!force && lastFetchTime && (Date.now() - lastFetchTime) < CACHE_DURATION) {
      return;
    }

    try {
      setLoading(true);
      const data = await fetchWhiteLabelBranding();

      // Handle unregistered domain case
      if (data.isUnregisteredDomain) {
        setIsUnregisteredDomain(true);
        setDomainInfo({ domain: data.domain });
        setBranding(defaultBranding);
        applyBranding(defaultBranding);
      } else {
        setIsUnregisteredDomain(false);
        setDomainInfo(null);
        const processedBranding = processBrandingData(data);
        setBranding(processedBranding);
        applyBranding(processedBranding);
      }

      setLastFetchTime(Date.now());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const isWhiteLabel = () => branding.isWhiteLabel;

  const getAppName = () => {
    if (!branding.isWhiteLabel) {
      return 'Zoobbe';
    }

    // For white label, derive from branding or domain
    if (branding.appName) {
      return branding.appName;
    }

    // Derive from domain
    const hostname = window.location.hostname;
    if (hostname.endsWith('.zoobbe.com')) {
      const subdomain = hostname.split('.')[0];
      return subdomain.charAt(0).toUpperCase() + subdomain.slice(1);
    } else if (!hostname.includes('localhost')) {
      const domainParts = hostname.split('.');
      if (domainParts.length >= 2) {
        const mainDomain = domainParts[domainParts.length - 2];
        return mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1);
      }
    }

    return 'App'; // Neutral fallback for WL
  };

  const getLogo = (theme) => {
    // If theme is specified and dark logo exists, return dark logo for dark theme
    if (theme === 'dark' && branding.logoDark) {
      return branding.logoDark;
    }
    // Otherwise return the light logo
    return branding.logo;
  };

  const getLogoDark = () => branding.logoDark;

  const getLogoIcon = () => branding.logoIcon || branding.logo;

  const getPrimaryColor = () => branding.primaryColor || '#0966ff';

  const shouldHideZoobbeBranding = () => branding.hideZoobbeBranding && branding.isWhiteLabel;

  const getSupportEmail = () => {
    if (!branding.isWhiteLabel) {
      return 'support@zoobbe.com';
    }

    // For white label, derive from branding or domain
    if (branding.supportEmail) {
      return branding.supportEmail;
    }

    // Derive from domain
    const hostname = window.location.hostname;
    if (!hostname.includes('localhost') && !hostname.endsWith('.zoobbe.com')) {
      return `support@${hostname}`;
    }

    return 'support@example.com'; // Neutral fallback for WL
  };

  const getCustomFooter = () => branding.customFooter;

  const getTermsUrl = () => branding.customTermsUrl || '/terms';

  const getPrivacyUrl = () => branding.customPrivacyUrl || '/privacy';

  const canSignup = () => {
    if (!branding.isWhiteLabel) return true;
    return branding.features.allowPublicSignup;
  };

  // OAuth helper functions
  const isGoogleAuthEnabled = () => {
    if (!branding.isWhiteLabel) {
      // For main Zoobbe domain, check global config
      return config.ENABLE_GOOGLE_AUTH;
    }
    return branding.oauth?.enableGoogleAuth || false;
  };

  const isGithubAuthEnabled = () => {
    if (!branding.isWhiteLabel) {
      // For main Zoobbe domain, check global config
      return config.ENABLE_GITHUB_AUTH;
    }
    return branding.oauth?.enableGithubAuth || false;
  };

  const getGoogleClientId = () => {
    if (!branding.isWhiteLabel) return null; // Use default for regular Zoobbe
    return branding.oauth?.googleClientId || null;
  };

  const getGithubClientId = () => {
    if (!branding.isWhiteLabel) return null; // Use default for regular Zoobbe
    return branding.oauth?.githubClientId || null;
  };

  // Preloader helper functions
  const getPreloaderConfig = () => branding.preloader || {};

  const isPreloaderEnabled = () => {
    const config = getPreloaderConfig();
    return config.enabled !== false; // Default to true if not specified
  };

  const getPreloaderType = () => {
    const config = getPreloaderConfig();
    return config.type || 'default';
  };

  const getPreloaderCustomLogo = () => {
    const config = getPreloaderConfig();
    return config.customLogo;
  };

  const shouldShowPreloaderFromText = () => {
    const config = getPreloaderConfig();
    return config.showFromText !== false; // Default to true if not specified
  };

  const getPreloaderBackgroundColor = () => {
    const config = getPreloaderConfig();
    return config.backgroundColor;
  };

  const getPreloaderLogoSize = () => {
    const config = getPreloaderConfig();
    return config.logoSize || 'medium';
  };

  const getPreloaderAnimationType = () => {
    const config = getPreloaderConfig();
    return config.animationType || 'fade';
  };

  const contextValue = {
    // State
    branding,
    loading,
    error,
    isUnregisteredDomain,
    domainInfo,

    // Actions
    updateBranding,
    refreshBranding,

    // Helper functions
    isWhiteLabel,
    getAppName,
    getLogo,
    getLogoDark,
    getLogoIcon,
    getPrimaryColor,
    shouldHideZoobbeBranding,
    getSupportEmail,
    getCustomFooter,
    getTermsUrl,
    getPrivacyUrl,
    canSignup,

    // OAuth functions
    isGoogleAuthEnabled,
    isGithubAuthEnabled,
    getGoogleClientId,
    getGithubClientId,

    // Preloader functions
    getPreloaderConfig,
    isPreloaderEnabled,
    getPreloaderType,
    getPreloaderCustomLogo,
    shouldShowPreloaderFromText,
    getPreloaderBackgroundColor,
    getPreloaderLogoSize,
    getPreloaderAnimationType,

    // Raw branding object for advanced use
    rawBranding: branding
  };

  return (
    <WhiteLabelContext.Provider value={contextValue}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

// Custom hook to use white label context
export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  
  if (context === undefined) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  
  return context;
};

// HOC for components that need white label context
export const withWhiteLabel = (Component) => {
  return function WhiteLabelComponent(props) {
    const whiteLabelContext = useWhiteLabel();
    return <Component {...props} whiteLabel={whiteLabelContext} />;
  };
};

// Utility functions for external access to branding
export const getGlobalBranding = () => globalBranding;

export const reapplyBranding = () => {
  if (globalBranding) {
    applyBranding(globalBranding);
  }
};

export const isGlobalWhiteLabel = () => {
  return globalBranding && globalBranding.isWhiteLabel;
};

export const getGlobalPrimaryColor = () => {
  return globalBranding ? globalBranding.primaryColor : '#0966ff';
};

// Function to ensure branding takes priority over theme changes
export const ensureBrandingPriority = () => {
  if (globalBranding && globalBranding.isWhiteLabel && globalBranding.primaryColor) {
    // Force reapply with a slight delay to override theme changes
    setTimeout(() => {
      applyBranding(globalBranding);
    }, 10);
  }
};

export default WhiteLabelContext;
